import classNames from 'classnames/bind';
import { IOta, OtaType } from 'detail/types';
import { useState } from 'react';
import Information from './Information';
import styles from './index.module.scss';

const cx = classNames.bind(styles);

interface IProps {
  otaInfo: IOta;
  isFreeSameDayRefundable?: boolean;
  clickCodePrefix: string;
  otaIndex?: number;
}

export function AgentInfo({
  otaInfo,
  isFreeSameDayRefundable,
  clickCodePrefix,
  otaIndex,
}: IProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { caution, csInfo, expName: otaName, otaType } = otaInfo;
  const isGlobal =
    otaType === OtaType.GLOBAL_AIRLINE || otaType === OtaType.GLOBAL_OTA;
  const {
    active,
    phone1,
    phone2,
    businessHour,
    lunchTime,
    is365Days,
    email,
    online,
    otherContactInfo,
  } = csInfo || {};
  const hasCsInfo =
    phone1 ||
    phone2 ||
    businessHour ||
    lunchTime ||
    email ||
    online ||
    otherContactInfo;

  return (
    <details
      className={cx('AgentInfo')}
      data-event-nlog="click"
      data-event-area-code={`${clickCodePrefix}.otainfo${isOpen ? 'close' : otaIndex}`}
      onToggle={e => {
        setIsOpen(e.currentTarget.open);
      }}
    >
      <summary className={cx('summary')}>
        <div className={cx('ota')}>
          <span className={cx('name')}>{otaName}</span>
          <i className={cx('icon')} title="여행사 정보 상세보기 토글" />
        </div>
      </summary>
      <div className={cx('detail')} role="tooltip">
        <h5 className={cx('otaname')}>{otaName}</h5>
        {active && hasCsInfo && (
          <div className={cx('customer_center')}>
            <h5 className={cx('title')}>고객센터</h5>
            <dl className={cx('list')}>
              {phone1 && (
                <>
                  <dt className={cx('name')}>대표전화</dt>
                  <dd className={cx('value')}>{phone1}</dd>
                </>
              )}
              {phone2 && (
                <>
                  <dt className={cx('name')}>추가 연락처</dt>
                  <dd className={cx('value')}>{phone2}</dd>
                </>
              )}
              {businessHour && (
                <>
                  <dt className={cx('name')}>운영시간</dt>
                  <dd className={cx('value')}>
                    {businessHour}
                    {is365Days ? ' (연중무휴)' : ' (월~금)'}
                  </dd>
                </>
              )}
              {lunchTime && (
                <>
                  <dt className={cx('name')}>점심시간</dt>
                  <dd className={cx('value')}>{lunchTime}</dd>
                </>
              )}
              {email && (
                <>
                  <dt className={cx('name')}>이메일 문의</dt>
                  <dd className={cx('value')}>{email}</dd>
                </>
              )}
              {online && (
                <>
                  <dt className={cx('name')}>온라인 문의</dt>
                  <dd className={cx('value')}>{online}</dd>
                </>
              )}
              {otherContactInfo && (
                <>
                  <dt className={cx('name')}>기타</dt>
                  <dd className={cx('value')}>{otherContactInfo}</dd>
                </>
              )}
            </dl>
          </div>
        )}
        {isFreeSameDayRefundable && (
          <div className={cx('desc_freeSameDayRefundable')}>
            <b className={cx('label')}>결제당일 무료취소</b>
            <p className={cx('text')}>
              <strong>
                <b>결제당일 무료취소</b> 항공권은
                <br />
                <b>결제 당일 예약 취소 시 전액 환불</b>됩니다.
              </strong>
              <br />
              단, 여행사 취소 불가 시간(23:50~00:00)과 항공사 시스템
              점검시간(항공사별 상이)에는 환불 및 취소가 불가합니다.
            </p>
          </div>
        )}
        {active && (
          <Information otaInfo={otaInfo} clickCodePrefix={clickCodePrefix} />
        )}
        {active && (caution || isGlobal) && (
          <div className={cx('disclaimer')}>
            <h5 className={cx('title')}>주의사항</h5>
            <ol className={cx('list')}>
              {caution && <li className={cx('item')}>{caution}</li>}
              {isGlobal && (
                <>
                  <li className={cx('item')}>
                    {otaName}은/는 환불 불가 항공권을 판매하는 경우가 있으니
                    주의하세요.
                  </li>
                  <li className={cx('item')}>
                    결제 신용카드에 해외원화결제 차단 설정이 미적용된 경우, 추가
                    수수료가 발생할 수 있습니다.
                  </li>
                  <li className={cx('item')}>
                    해외지역 출발 항공권은 {otaName} 페이지의 항공권 가격이
                    출발지의 통화로 표시될 수 있으며, 환율에 따라 네이버에서
                    확인한 원화 금액과 차이가 발생할 수 있습니다.
                  </li>
                </>
              )}
            </ol>
          </div>
        )}
      </div>
    </details>
  );
}

export default AgentInfo;
